import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSalesService } from 'services/sales';
import { useSalesData } from 'hooks/useSalesData';
import { ROUTES } from 'utils/constants';
import { Sales as SalesType, SalesOptions } from 'types/sales';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import {
  Plus,
  Search,
  Eye,
  Trash2,
  CheckCircle2,
  Circle,
  Users,
  CreditCard,
  Package,
} from 'lucide-react';
import VoidSalesModal from './VoidSalesModal';
import styles from './Sales.module.scss';

function Sales() {
  const navigate = useNavigate();
  const salesService = useSalesService();
  const [salesOptions, setSalesOptions] = useState<SalesOptions | null>(null);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [saleToVoid, setSaleToVoid] = useState<SalesType | null>(null);
  const [isVoiding, setIsVoiding] = useState(false);

  // Use sales data hook
  const { sales, loading, reloading, error, reload, updateSale } = useSalesData(
    { includeVoided },
  );
  const initialLoading = loading && sales.length === 0;

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  // Load sales options
  const loadSalesOptions = useCallback(async () => {
    try {
      const options = await salesService.getSalesOptions();
      setSalesOptions(options);
    } catch {
      // Silently fail - we'll just check if options are null
      setSalesOptions(null);
    }
  }, [salesService]);

  useEffect(() => {
    void loadSalesOptions();
  }, [loadSalesOptions]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Filter sales based on search
  const filteredSales = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return sales.filter((sale) => {
      const matchesSearch =
        sale.description.toLowerCase().includes(searchLower) ||
        sale.date.toLowerCase().includes(searchLower) ||
        (sale.customer?.name?.toLowerCase().includes(searchLower) ?? false);
      return matchesSearch;
    });
  }, [sales, searchQuery]);

  const totalFilteredSales = filteredSales.length;

  const paginatedSales = useMemo(() => {
    if (totalFilteredSales === 0) {
      return [];
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredSales.slice(startIndex, endIndex);
  }, [filteredSales, currentPage, pageSize, totalFilteredSales]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, includeVoided]);

  useEffect(() => {
    const nextTotalPages = Math.max(
      1,
      Math.ceil(totalFilteredSales / pageSize),
    );
    setCurrentPage((prevPage) => Math.min(prevPage, nextTotalPages));
  }, [totalFilteredSales, pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleViewSale = (sale: SalesType) => {
    navigate(`${ROUTES.SALES.ROOT}/${sale.id}`);
  };

  const handleCreateSale = () => {
    navigate(ROUTES.SALES.ADD);
  };

  const handleVoidSale = (sale: SalesType) => {
    setSaleToVoid(sale);
  };

  const handleCloseVoidModal = () => {
    setSaleToVoid(null);
  };

  const handleConfirmVoid = useCallback(
    async (reason: string) => {
      if (!saleToVoid) return;

      setIsVoiding(true);
      try {
        const voidedSale = await salesService.voidSale(
          saleToVoid.id,
          reason || undefined,
        );
        // Update the sale in state and cache
        updateSale(voidedSale);
        showToast('Sale voided successfully', 'success');
        setSaleToVoid(null);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to void sale. Please try again.';
        showToast(errorMessage, 'error');
      } finally {
        setIsVoiding(false);
      }
    },
    [saleToVoid, salesService, showToast, updateSale],
  );

  // Check if prerequisites are met
  const hasCustomers =
    salesOptions?.customers && salesOptions.customers.length > 0;
  const hasPaymentMethods =
    salesOptions?.paymentMethods && salesOptions.paymentMethods.length > 0;
  const hasProducts =
    salesOptions?.products && salesOptions.products.length > 0;
  const canCreateSale = hasCustomers && hasPaymentMethods && hasProducts;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Sales</h1>
        <div className={styles.headerButtons}>
          <RefreshButton onClick={reload} loading={reloading} />
          {canCreateSale && (
            <Button onClick={handleCreateSale}>
              <Plus size={18} style={{ marginRight: '0.5rem' }} />
              New Sale
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {canCreateSale && (
        <div className={styles.filters}>
          <div className={styles.searchContainer}>
            <Search size={18} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search sales..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className={styles.searchInput}
            />
          </div>
          <label className={styles.filterLabel}>
            <input
              type="checkbox"
              checked={includeVoided}
              onChange={(e) => {
                setIncludeVoided(e.target.checked);
              }}
              className={styles.filterCheckbox}
            />
            Include Voided
          </label>
        </div>
      )}

      {/* Error State */}
      {error && sales.length === 0 && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={reload}>Retry</Button>
        </div>
      )}

      {/* Prerequisites Checklist */}
      {!canCreateSale && salesOptions && (
        <div className={styles.prerequisitesCard}>
          <h2 className={styles.prerequisitesTitle}>
            Before You Can Make Your First Sale
          </h2>
          <p className={styles.prerequisitesDescription}>
            You need to set up the following before creating a sale:
          </p>
          <ul className={styles.prerequisitesList}>
            <li className={styles.prerequisitesItem}>
              {hasCustomers ? (
                <CheckCircle2 size={20} className={styles.checkIcon} />
              ) : (
                <Circle size={20} className={styles.circleIcon} />
              )}
              <div className={styles.prerequisitesItemContent}>
                <div className={styles.prerequisitesItemTitle}>
                  <Users size={18} />
                  <span>Set up Customers</span>
                </div>
                {!hasCustomers && (
                  <p className={styles.prerequisitesItemDescription}>
                    You need at least one customer to create a sale. Customers
                    represent the people or businesses you sell to.
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(ROUTES.CUSTOMERS.ROOT)}
                  className={styles.prerequisitesButton}
                >
                  Go to Customers
                </Button>
              </div>
            </li>
            <li className={styles.prerequisitesItem}>
              {hasPaymentMethods ? (
                <CheckCircle2 size={20} className={styles.checkIcon} />
              ) : (
                <Circle size={20} className={styles.circleIcon} />
              )}
              <div className={styles.prerequisitesItemContent}>
                <div className={styles.prerequisitesItemTitle}>
                  <CreditCard size={18} />
                  <span>Set up Payment Methods</span>
                </div>
                {!hasPaymentMethods && (
                  <p className={styles.prerequisitesItemDescription}>
                    You need at least one payment method to record how customers
                    pay for their purchases (e.g., Cash, Credit Card, Bank
                    Transfer).
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(ROUTES.DASHBOARD)}
                  className={styles.prerequisitesButton}
                >
                  Go to Dashboard
                </Button>
              </div>
            </li>
            <li className={styles.prerequisitesItem}>
              {hasProducts ? (
                <CheckCircle2 size={20} className={styles.checkIcon} />
              ) : (
                <Circle size={20} className={styles.circleIcon} />
              )}
              <div className={styles.prerequisitesItemContent}>
                <div className={styles.prerequisitesItemTitle}>
                  <Package size={18} />
                  <span>Set up Products or Services</span>
                </div>
                {!hasProducts && (
                  <p className={styles.prerequisitesItemDescription}>
                    You need at least one product or service to sell. Products
                    are items you sell, and services are non-physical offerings.
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(ROUTES.PRODUCTS.ROOT)}
                  className={styles.prerequisitesButton}
                >
                  Go to Products
                </Button>
              </div>
            </li>
          </ul>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredSales.length === 0 && !error && canCreateSale && (
        <div className={styles.emptyState}>
          <p>
            {searchQuery || includeVoided
              ? 'No sales found matching your filters.'
              : 'No sales yet. Create your first sale to get started.'}
          </p>
        </div>
      )}

      {/* Sales Table */}
      {(initialLoading || filteredSales.length > 0) && (
        <div className={styles.tableContainer}>
          {!initialLoading && reloading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}>Loading...</div>
            </div>
          )}
          {initialLoading ? (
            <div className={styles.tableSkeleton}>
              <div className={styles.tableSkeletonHeader}>
                {[
                  'Date',
                  'Description',
                  'Total Revenue',
                  'Status',
                  'Actions',
                ].map((label) => (
                  <div key={label} className={styles.tableSkeletonHeaderCell}>
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                    />
                  </div>
                ))}
              </div>
              <div className={styles.tableSkeletonBody}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={`sales-skeleton-${index}`}
                    className={styles.skeletonRow}
                  >
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                    />
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonLong}`}
                    />
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                    />
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                    />
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Total Revenue</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSales.map((sale) => (
                  <tr key={sale.id}>
                    <td className={styles.dateCell}>{formatDate(sale.date)}</td>
                    <td className={styles.descriptionCell}>
                      {sale.description || 'No description'}
                    </td>
                    <td className={styles.amountCell}>
                      ${formatCurrency(sale.totalRevenue)}
                    </td>
                    <td className={styles.statusCell}>
                      {sale.isVoided ? (
                        <span className={styles.voidedBadge}>Voided</span>
                      ) : (
                        <span className={styles.activeBadge}>Active</span>
                      )}
                    </td>
                    <td className={styles.actionsCell}>
                      <button
                        className={styles.viewButton}
                        onClick={() => {
                          handleViewSale(sale);
                        }}
                        aria-label={`View sale ${sale.id}`}
                        title="View sale"
                      >
                        <Eye size={16} />
                      </button>
                      {!sale.isVoided && (
                        <button
                          className={styles.voidButton}
                          onClick={() => {
                            handleVoidSale(sale);
                          }}
                          aria-label={`Void sale ${sale.id}`}
                          title="Void sale"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalFilteredSales > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalFilteredSales}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}

      {/* Void Modal */}
      <VoidSalesModal
        show={!!saleToVoid}
        onHide={handleCloseVoidModal}
        sale={saleToVoid}
        onConfirm={(reason) => void handleConfirmVoid(reason)}
        isPending={isVoiding}
      />
    </div>
  );
}

export default Sales;
